import React, { useState, useEffect } from 'react';
import { Download, FileText, Search, X, Calendar, User, ClipboardList, Send, CheckCircle, Eye } from 'lucide-react';
import api from '../api';
import { useApp } from '../context/AppContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Reports = () => {
    const { t } = useApp();
    const [reports, setReports] = useState([]);
    const [resolvedComplaints, setResolvedComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedReport, setSelectedReport] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Send Report Form State (For Admins)
    const [reportForm, setReportForm] = useState({ complaintId: '', title: '', recipientEmail: '' });

    const user = JSON.parse(localStorage.getItem('user')) || {};
    const isAdmin = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';

    const fetchData = async () => {
        setLoading(true);
        try {
            const endpoint = isAdmin ? '/api/reports/all' : '/api/reports/my-reports';
            const res = await api.get(endpoint);
            setReports(res.data);

            if (isAdmin) {
                const complaintsRes = await api.get('/api/complaints');
                const resolved = complaintsRes.data.filter(c => c.status === 'Resolved' || c.status === 'Closed');
                setResolvedComplaints(resolved);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDownloadPDF = (report) => {
        if (!report) return;

        try {
            // Robust initialization
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.width;

            // Premium Header Background
            doc.setFillColor(37, 99, 235);
            doc.rect(0, 0, 210, 50, 'F');

            // Header Typography
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(26);
            doc.setFont('helvetica', 'bold');
            doc.text('RESOLUTION REPORT', pageWidth / 2, 28, { align: 'center' });

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('OFFICIAL SYSTEM VERIFICATION • DOCUMENT GENERATED BY TMS', pageWidth / 2, 38, { align: 'center' });

            // Document Details
            doc.setTextColor(30, 41, 59);
            const reportDate = report.createdAt ? new Date(report.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB');
            const ticketId = report.complaint?._id ? `#CMP-${report.complaint._id.slice(-6).toUpperCase()}` : 'N/A';

            const startY = 65;
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('VERIFICATION DETAILS', 20, startY);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(`Official Date: ${reportDate}`, 20, startY + 8);
            doc.text(`Tracking ID: ${ticketId}`, 150, startY + 8);

            // Safer Data Transformation
            const safeData = [
                ['Report Title', String(report.title || 'Service Resolution')],
                ['Authorized Recipient', `${String(report.recipient?.name || 'User')} (${String(report.recipient?.email || 'N/A')})`],
                ['Ticket Case', String(report.complaint?.title || 'N/A')],
                ['Service Category', String(report.complaint?.type || 'N/A')],
                ['Unit / Location', String(report.complaint?.location || 'N/A')],
                ['Status at Export', String(report.complaint?.status || 'Resolved')],
                ['System Auditor', String(report.generatedBy?.name || 'Authorized Administrator')]
            ];

            // AutoTable Registration Check
            if (typeof doc.autoTable === 'function') {
                doc.autoTable({
                    startY: startY + 15,
                    head: [['SPECIFICATION', 'DOCUMENTED STATUS']],
                    body: safeData,
                    theme: 'grid',
                    headStyles: { fillColor: [37, 99, 235], fontSize: 11, halign: 'center', fontStyle: 'bold' },
                    styles: { cellPadding: 6, fontSize: 10, textColor: [51, 65, 85], lineColor: [226, 232, 240] },
                    columnStyles: {
                        0: { fontStyle: 'bold', cellWidth: 50, fillColor: [248, 250, 252] }
                    }
                });
            } else {
                console.error('jsPDF-AutoTable not found on doc instance');
                // Fallback basic text if table fails
                let fallbackY = startY + 25;
                safeData.forEach(([label, value]) => {
                    doc.text(`${label}: ${value}`, 20, fallbackY);
                    fallbackY += 10;
                });
            }

            // Footer Section
            const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 35 : 220;
            doc.setDrawColor(226, 232, 240);
            doc.line(20, finalY - 5, 190, finalY - 5);

            doc.setFontSize(9);
            doc.setTextColor(148, 163, 184);
            doc.text('This is an electronically transmitted official document of the Ticket Management System.', pageWidth / 2, finalY + 2, { align: 'center' });
            doc.text('Verification completed by Technical Operations Team.', pageWidth / 2, finalY + 8, { align: 'center' });

            // Trigger Save
            const safeFileName = (report.title || 'Resolution_Report').replace(/[^a-z0-9]/gi, '_');
            doc.save(`${safeFileName}_Official.pdf`);

            alert('Official Resolution Report Saved Successfully! 📄✅');
        } catch (err) {
            console.error('PDF Generation Failure:', err);
            alert('PDF Generation failed. This could be due to missing ticket records. Please refresh and try again.');
        }
    };

    const handleViewReport = (report) => {
        setSelectedReport(report);
        setIsViewModalOpen(true);
    };

    const handleSendReport = async (e) => {
        e.preventDefault();
        try {
            await api.post('/api/reports', reportForm);
            setReportForm({ complaintId: '', title: '', recipientEmail: '' });
            alert('Report generated and sent successfully!');
            fetchData();
        } catch (err) {
            alert('Error generating report');
        }
    };

    const handleComplaintSelect = (id) => {
        const complaint = resolvedComplaints.find(c => c._id === id);
        if (complaint) {
            setReportForm({
                complaintId: id,
                title: `Resolution Report: ${complaint.title}`,
                recipientEmail: complaint.raisedBy?.email || ''
            });
        }
    };

    return (
        <div style={{ animation: 'fadeIn 0.5s ease-out', paddingBottom: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                <div>
                    <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b' }}>{isAdmin ? 'Management Reports' : t('system_reports')}</h1>
                    <p style={{ color: '#64748b', marginTop: 4 }}>Access your official resolution documents and certificates.</p>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="Search reports..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ padding: '12px 16px', paddingLeft: 44, borderRadius: 14, border: '1.5px solid #e2e8f0', background: 'white', width: 300, outline: 'none', transition: '0.2s' }}
                        />
                        <Search size={18} style={{ position: 'absolute', left: 16, top: 13, color: '#94a3b8' }} />
                    </div>
                    <div style={{ background: '#3b82f6', color: 'white', padding: '10px 20px', borderRadius: 12, fontSize: 14, fontWeight: 600 }}>
                        {reports.length} Reports
                    </div>
                </div>
            </div>

            {isAdmin && (
                <div className="white-card" style={{ padding: '54px', borderRadius: 28, marginBottom: 40, boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.08)', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 44 }}>
                        <h2 style={{ fontSize: 30, fontWeight: 800, color: '#101828', letterSpacing: '-0.02em' }}>Send Resolution Report</h2>
                        <div style={{ background: '#f0fdf4', padding: '8px 16px', borderRadius: 12, color: '#16a34a', fontSize: 14, fontWeight: 600 }}>Admin Panel</div>
                    </div>
                    <form onSubmit={handleSendReport} style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
                            <label style={{ fontSize: 20, fontWeight: 700, color: '#334155', minWidth: 180 }}>Select Task</label>
                            <select
                                value={reportForm.complaintId}
                                onChange={(e) => handleComplaintSelect(e.target.value)}
                                style={{ flex: 1, padding: '14px 22px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 17, color: '#1e293b', outline: 'none' }}
                                required
                            >
                                <option value="">Select a completed complaint...</option>
                                {resolvedComplaints.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                            </select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
                            <label style={{ fontSize: 20, fontWeight: 700, color: '#334155', minWidth: 180 }}>Report Title</label>
                            <input type="text" value={reportForm.title} onChange={(e) => setReportForm({ ...reportForm, title: e.target.value })} style={{ flex: 1, padding: '14px 22px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 17, outline: 'none' }} required />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
                                <label style={{ fontSize: 20, fontWeight: 700, color: '#334155', minWidth: 180 }}>Recipient Email</label>
                                <input type="email" placeholder="user@example.com" value={reportForm.recipientEmail} onChange={(e) => setReportForm({ ...reportForm, recipientEmail: e.target.value })} style={{ flex: 1, padding: '14px 22px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 17, outline: 'none' }} required />
                            </div>
                            <p style={{ fontSize: 15, color: '#64748b', marginLeft: 208 }}>The report will be sent to this user's dashboard.</p>
                        </div>
                        <button type="submit" className="btn" style={{ width: '100%', padding: '22px', background: '#3b82f6', color: 'white', borderRadius: 18, fontSize: 22, fontWeight: 700, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, border: 'none', cursor: 'pointer' }}>
                            <Send size={26} /> Send Report
                        </button>
                    </form>
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>Loading reports...</div>
                ) : reports.filter(r => r.title.toLowerCase().includes(searchQuery.toLowerCase())).length > 0 ? reports.filter(r => r.title.toLowerCase().includes(searchQuery.toLowerCase())).map((report) => (
                    <div
                        key={report._id}
                        className="white-card"
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: '0.3s', padding: '20px 24px', borderRadius: 20 }}
                    >
                        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                            <div style={{ background: '#eff6ff', padding: 14, borderRadius: 16 }}><FileText size={24} color="#3b82f6" /></div>
                            <div>
                                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{report.title}</h3>
                                <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{new Date(report.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} • {report.complaint?.type}</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button className="btn" style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => handleViewReport(report)}>
                                <Eye size={18} color="#64748b" />
                            </button>
                            <button className="btn" style={{ background: '#3b82f6', color: 'white', padding: '12px 16px', borderRadius: 12, border: 'none', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => handleDownloadPDF(report)}>
                                <Download size={18} />
                            </button>
                        </div>
                    </div>
                )) : (
                    <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 20, color: '#64748b' }}>No reports found matching your criteria.</div>
                )}
            </div>

            {isViewModalOpen && selectedReport && (
                <div className="modal-overlay" style={{ background: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(12px)', zIndex: 1000 }}>
                    <div className="modal-content" style={{ maxWidth: 800, padding: 0, borderRadius: 32, overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                        {/* Professional Letterhead Header */}
                        <div style={{ background: '#3b82f6', padding: '40px 60px', color: 'white', position: 'relative' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-1px', margin: 0 }}>OFFICIAL RESOLUTION</h1>
                                    <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: 16 }}>Ticket Management System • Document ID: {selectedReport._id.toUpperCase()}</p>
                                </div>
                                <button onClick={() => setIsViewModalOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: 12, borderRadius: 16, cursor: 'pointer', transition: '0.2s' }}>
                                    <X size={24} />
                                </button>
                            </div>
                            <div style={{ position: 'absolute', right: 60, bottom: -40, width: 80, height: 80, background: 'white', borderRadius: 24, display: 'grid', placeContent: 'center', boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)' }}>
                                <CheckCircle size={40} color="#3b82f6" />
                            </div>
                        </div>

                        {/* Document Content */}
                        <div style={{ padding: '80px 60px 60px', background: 'white' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
                                <div>
                                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 20, letterSpacing: '0.1em' }}>Report Overview</h3>
                                    <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>{selectedReport.title}</h2>
                                    <p style={{ fontSize: 16, color: '#64748b', lineHeight: 1.6 }}>This document certifies the successful resolution of the service request detailed below. Our technical team has verified the fix according to the system's operational standards.</p>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, padding: 32, background: '#f8fafc', borderRadius: 24, border: '1px solid #e2e8f0' }}>
                                    <div>
                                        <p style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Recipient Information</p>
                                        <p style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: 0 }}>{selectedReport.recipient?.name || 'Authorized User'}</p>
                                        <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>{selectedReport.recipient?.email}</p>
                                    </div>
                                    <div>
                                        <p style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Task Reference</p>
                                        <p style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: 0 }}>{selectedReport.complaint?.title}</p>
                                        <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>Location: {selectedReport.complaint?.location} • Category: {selectedReport.complaint?.type}</p>
                                    </div>
                                    <div>
                                        <p style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Date Generated</p>
                                        <p style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: 0 }}>{new Date(selectedReport.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                    </div>
                                    <div>
                                        <p style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Verified By</p>
                                        <p style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: 0 }}>{selectedReport.generatedBy?.name || 'System Administrator'}</p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                                    <button
                                        onClick={() => handleDownloadPDF(selectedReport)}
                                        style={{ flex: 1, height: 64, background: '#3b82f6', color: 'white', borderRadius: 20, fontSize: 18, fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, boxShadow: '0 10px 20px -5px rgba(59, 130, 246, 0.3)' }}
                                    >
                                        <Download size={22} /> Download Official PDF
                                    </button>
                                    <button
                                        onClick={() => setIsViewModalOpen(false)}
                                        style={{ width: 140, height: 64, background: '#f1f5f9', color: '#64748b', borderRadius: 20, fontSize: 16, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reports;
